'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

// Theme Constants
const GOLD_ACCENT = '#faaf40';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';

export default function JoinPopup() {
  const [isVisible, setIsVisible] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    designation: '',
    goal: 'General Fitness & Conditioning',
  });
  
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // --- POPUP LOGIC ---
  useEffect(() => {
    // 1. Check if user has seen popup in this session
    const hasSeenPopup = sessionStorage.getItem('aura_popup_seen');

    if (!hasSeenPopup) {
      // 2. Show after 3 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('aura_popup_seen', 'true');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('Submitting...');

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('Welcome to the Club! We will contact you shortly.');

        // ==========================================
        //  FACEBOOK PIXEL: LEAD EVENT
        // ==========================================
        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', 'Lead', {
                content_name: 'Popup Form', 
                content_category: 'EMS & Pilates',
                currency: 'PKR',
                value: 0.00,
                city: formData.city,
                service_type: formData.goal
            }, {
                // Advanced Matching
                em: formData.email, 
                ph: formData.phone, 
                fn: formData.name, 
                ct: formData.city
            });
        }
        // ==========================================
        
        // Auto Close after 2 seconds on success
        setTimeout(() => {
           handleClose();
           setStatus('');
           setFormData({ name: '', email: '', phone: '', city: '', designation: '', goal: 'General Fitness & Conditioning' });
        }, 2000);

      } else {
        setStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setStatus('Error submitting form.');
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const inputClasses = `
    w-full bg-[#1a1a1a] text-white border border-white/20 rounded-lg 
    px-4 py-3 outline-none transition-all duration-300
    focus:border-[color:var(--gold)] focus:ring-1 focus:ring-[color:var(--gold)]
    placeholder-gray-500 text-sm
  `;

  const labelClasses = "block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wide";

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* BACKDROP */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
          >
            {/* MODAL */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-[#faaf40]/30 rounded-2xl shadow-2xl"
              style={{ '--gold': GOLD_ACCENT, '--shadow-gold': SHADOW_GOLD }}
            >
              
              {/* Close Button */}
              <button 
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-white z-20 bg-black/50 rounded-full p-1"
              >
                <X size={24} />
              </button>

              {/* Decorative Glow */}
              <div 
                className="absolute top-0 right-0 w-64 h-64 bg-[#faaf40] blur-[100px] opacity-10 pointer-events-none"
              />

              <div className="p-6 sm:p-8">
                
                {/* Header */}
                <div className="text-center mb-6">
                  <h2 
                    className="text-2xl sm:text-3xl font-extrabold uppercase italic mb-2" 
                    style={{ color: GOLD_ACCENT, textShadow: `0 0 10px ${SHADOW_GOLD}` }}
                  >
                    Start Your Journey
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Exclusive memberships are limited. Secure your spot.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  
                  {/* Name */}
                  <div>
                    <label className={labelClasses}>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="ENTER YOUR FULL NAME"
                      className={inputClasses}
                    />
                  </div>

                  {/* Phone & Email Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClasses}>Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="0300-XXXXXXX"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="EMAIL"
                        className={inputClasses}
                      />
                    </div>
                  </div>

                  {/* City & Profession Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClasses}>City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="ISLAMABAD"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Profession</label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                        placeholder="JOB / TITLE"
                        className={inputClasses}
                      />
                    </div>
                  </div>

                  {/* Goal Dropdown */}
                  <div>
                    <label className={labelClasses}>Your Goal</label>
                    <div className="relative">
                      <select
                        name="goal"
                        value={formData.goal}
                        onChange={handleChange}
                        className={`${inputClasses} appearance-none cursor-pointer`}
                      >
                        <option value="General Fitness & Conditioning">General Fitness & Conditioning</option>
                        <option value="Weight Loss">Weight Loss</option>
                        <option value="Weight Gain / Muscle Building">Muscle Building</option>
                        <option value="EMS Training">EMS Training</option>
                        <option value="Pilates">Pilates</option>
                      </select>
                      {/* Chevron */}
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#faaf40]">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full font-bold text-black uppercase tracking-wider py-3 rounded-lg mt-2 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                    style={{ 
                      backgroundColor: GOLD_ACCENT,
                      boxShadow: `0 0 15px ${SHADOW_GOLD}` 
                    }}
                  >
                    {loading ? "Processing..." : "Join Now"}
                  </button>

                  {/* Status */}
                  {status && (
                    <div className={`text-center text-xs font-medium p-2 rounded ${status.includes('Welcome') ? 'text-green-400' : 'text-red-400'}`}>
                      {status}
                    </div>
                  )}

                </form>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}